import { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import i18n from '@/utils/i18n'
import { createOrder } from '@/api/orders'
import { listCompanies } from '@/api/companies'
import { listUsers } from '@/api/users'
import { listRatesK1 } from '@/api/tariffs'
import { getBillingPreview } from '@/api/billing'
import { useAuthStore } from '@/store/authStore'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useAddressSuggest } from '@/hooks/useAddressSuggest'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { AddressInput } from '@/components/ui/AddressInput'
import { formatPhoneInput } from '@/utils/phone'
import { formatGroupedInt, parseGroupedIntInput } from '@/utils/money-input'
import { formatLicensePlate, normalizeLicensePlate } from '@/utils/licensePlate'
import { validateVinSoft } from '@/utils/vinValidation'
import { VehicleSelect } from '@/components/orders/VehicleSelect'
import type { CalculateBillingResult } from '@/types/billing'

function serviceLabel(service: string): string {
  const map: Record<string, string> = {
    trip: i18n.t('orderDrawer.serviceTrip'),
    inspection: i18n.t('orderDrawer.serviceInspection'),
    deal: i18n.t('orderDrawer.serviceDeal'),
    delivery: i18n.t('orderDrawer.serviceDelivery'),
  }
  return map[service] ?? service
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="pt-1 text-xs font-semibold uppercase tracking-wider text-[color:var(--stryzh-color-text-secondary)]">
      {children}
    </p>
  )
}

const MANAGER_SELF_VALUE = '__self__'

/**
 * Дровер «Новый заказ». Реализует требования `SPECIFICATION_ADMIN.md` (раздел
 * про форму нового заказа) и `TARIFFS_SPEC.md` (§3 Скоп, §8 Расчёт).
 *
 * Ключевые особенности:
 *   - Блок «Заказчик» (компания + менеджер) показывается зависимо от роли:
 *     `admin`/`dispatcher` выбирают и компанию, и менеджера; `customer_supervisor`
 *     выбирает только менеджера в своей компании (по умолчанию — он сам);
 *     `manager` блок не видит — он сам и есть менеджер.
 *   - Поле «Оценочная цена» — целое число рублей с разделителями тысяч; пишется
 *     в `orders.desired_price` (триггер сам считает по нему К2 в млн ₽).
 *   - «Город выкупа» отдельным селектом больше нет — город определяется из
 *     адреса: либо из подсказки Яндекса (`onSuggestionPicked.locality`), либо
 *     прямым `ymaps.geocode` поверх введённой строки (через
 *     `useAddressSuggest.resolveLocality`). Если город не найден или не активен
 *     в `rates_k1` — форма блокируется с понятной ошибкой.
 *   - Sticky-панель снизу всегда видна и переходит из «данных недостаточно» в
 *     «расчёт готов», когда заполнены заказчик / адрес / цена и успешно
 *     отработал preview-billing (scope удалён в v2.0). Кнопка «Создать заказ» —
 *     на всю ширину панели; рядом — popover «Детализация» с разбивкой по услугам.
 */
export function OrderDrawer({
  open,
  onClose,
  onSaved,
}: {
  open: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)
  const role = profile?.role

  /* ── Режим блока «Заказчик» по роли ── */
  // admin/dispatcher выбирают компанию и менеджера руками; customer_supervisor — только
  // менеджера в своей компании; manager заказчика блок не видит вовсе.
  const customerMode: 'choose_company' | 'choose_manager' | 'self' =
    role === 'admin' || role === 'dispatcher'
      ? 'choose_company'
      : role === 'customer_supervisor'
        ? 'choose_manager'
        : 'self'

  /* ── Заказчик ── */
  const [companyId, setCompanyId] = useState<string>('')
  // ID менеджера ИЛИ MANAGER_SELF_VALUE (только для customer_supervisor по умолчанию).
  const [managerId, setManagerId] = useState<string>('')

  /* ── Автомобиль ── */
  const [brand, setBrand] = useState('')
  // id выбранной марки в справочнике легковых авто. В БД не пишется —
  // нужен только чтобы VehicleSelect мог подгрузить модели по brandId.
  const [brandId, setBrandId] = useState<number | null>(null)
  const [model, setModel] = useState('')
  const [vin, setVin] = useState('')
  /** Год выпуска (string в state для удобства <input>; парсим в int при save). */
  const [vehicleYear, setVehicleYear] = useState('')
  /** Гос. номер: state хранит уже отформатированную с пробелами строку
   *  (А 123 ВС 77); нормализуем перед записью в БД. */
  const [licensePlate, setLicensePlate] = useState('')
  const [estimatedPrice, setEstimatedPrice] = useState<number | null>(null)
  const [estimatedPriceText, setEstimatedPriceText] = useState('')

  /* ── Продавец ── */
  const [sellerName, setSellerName] = useState('')
  const [sellerPhone, setSellerPhone] = useState('+7 ')

  /* ── Место осмотра ── */
  const [address, setAddress] = useState('')
  // Город, распознанный из адреса (через подсказку или geocode). Сохраняется
  // вместе с исходной строкой адреса, чтобы при ручном вводе не пере-резолвить
  // тот же текст по кругу.
  const [resolvedCity, setResolvedCity] = useState<string | null>(null)
  const [resolvedForAddress, setResolvedForAddress] = useState<string>('')
  const [resolving, setResolving] = useState(false)

  /* ── Комментарий ── */
  const [comment, setComment] = useState('')

  /* ── Submit / ошибки ── */
  const [saving, setSaving] = useState(false)
  const [savingDraft, setSavingDraft] = useState(false)
  const [error, setError] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  /* ── Popover «Детализация» ── */
  const [popoverOpen, setPopoverOpen] = useState(false)

  /* ── Загрузка справочников ── */
  const { data: companiesData } = useQuery({
    queryKey: ['order-drawer', 'companies'],
    queryFn: () => listCompanies({ page: 1, pageSize: 200, type: 'customer' }),
    enabled: open && customerMode === 'choose_company',
    staleTime: 60_000,
  })
  const companies = companiesData?.items ?? []

  // Менеджеры/руководители выбранной компании. Для customer_supervisor — фиксируется
  // его собственная компания.
  const effectiveCompanyId = useMemo(() => {
    if (customerMode === 'choose_company') return companyId
    if (customerMode === 'choose_manager') return profile?.company_id ?? ''
    return profile?.company_id ?? ''
  }, [customerMode, companyId, profile?.company_id])

  const { data: managersData } = useQuery({
    queryKey: ['order-drawer', 'managers', effectiveCompanyId],
    queryFn: () =>
      listUsers({ page: 1, pageSize: 200, companyId: effectiveCompanyId }),
    enabled:
      open && customerMode !== 'self' && effectiveCompanyId.length > 0,
    staleTime: 30_000,
  })
  const managers = useMemo(
    () =>
      (managersData?.items ?? []).filter(
        (u) => u.role === 'manager' || u.role === 'customer_supervisor',
      ),
    [managersData],
  )

  const { data: k1Cities = [] } = useQuery({
    queryKey: ['order-drawer', 'rates-k1'],
    queryFn: () => listRatesK1(),
    enabled: open,
    staleTime: 60_000,
  })
  const activeCities = useMemo(
    () => k1Cities.filter((c) => c.is_active),
    [k1Cities],
  )
  const activeCitiesNames = useMemo(
    () => activeCities.map((c) => c.city),
    [activeCities],
  )

  /** Сопоставление распознанного города с активным городом К1 (case-insensitive). */
  const cityMatch = useMemo(() => {
    if (!resolvedCity) return null
    const lower = resolvedCity.toLowerCase()
    return activeCities.find((c) => c.city.toLowerCase() === lower) ?? null
  }, [resolvedCity, activeCities])

  /* ── Геокодер для ручного ввода адреса ── */
  const addressSuggest = useAddressSuggest({ knownCities: activeCitiesNames })
  const debouncedAddress = useDebouncedValue(address, 450)

  // Если пользователь ввёл адрес вручную и подсказку не выбрал — резолвим
  // город прямым геокодом. Запускаем не на каждый keystroke, а на отстоявшемся
  // значении адреса. Уже разрешённый адрес повторно не дёргаем.
  useEffect(() => {
    if (!open) return
    const text = debouncedAddress.trim()
    if (text.length < 3) return
    if (resolvedForAddress === text) return
    if (resolvedCity && address === resolvedForAddress) return
    let cancelled = false
    setResolving(true)
    addressSuggest
      .resolveLocality(text)
      .then((locality) => {
        if (cancelled) return
        setResolvedCity(locality ?? null)
        setResolvedForAddress(text)
      })
      .finally(() => {
        if (!cancelled) setResolving(false)
      })
    return () => {
      cancelled = true
    }
  }, [
    debouncedAddress,
    open,
    resolvedForAddress,
    resolvedCity,
    address,
    addressSuggest,
  ])

  /* ── Открытие/закрытие: сброс состояния и подгрузка дефолтов ── */
  useEffect(() => {
    if (!open) return
    setError('')
    setFieldErrors({})
    setPopoverOpen(false)
    if (customerMode === 'choose_manager') {
      // customer_supervisor: по умолчанию выбран он сам (см. SPECIFICATION_ADMIN.md).
      setManagerId(MANAGER_SELF_VALUE)
    } else if (customerMode === 'self') {
      setManagerId('')
    }
  }, [open, customerMode])

  /* ── manager_id и customer_company_id для submit ── */
  const dealerId = useMemo(() => {
    if (customerMode === 'self') return profile?.id ?? ''
    if (managerId === MANAGER_SELF_VALUE) return profile?.id ?? ''
    return managerId
  }, [customerMode, managerId, profile?.id])

  const customerCompanyId = useMemo(() => {
    if (customerMode === 'choose_company') return companyId
    return profile?.company_id ?? ''
  }, [customerMode, companyId, profile?.company_id])

  /* ── Preview-расчёт ── */
  const debouncedPrice = useDebouncedValue(estimatedPrice ?? 0, 300)
  const previewEnabled =
    open &&
    !!dealerId &&
    !!cityMatch &&
    debouncedPrice > 0
  const previewCity = cityMatch?.city ?? ''

  const previewQuery = useQuery<CalculateBillingResult>({
    queryKey: [
      'preview-billing',
      dealerId,
      previewCity,
      debouncedPrice,
    ],
    queryFn: () =>
      getBillingPreview({
        manager_id: dealerId,
        city: previewCity,
        estimated_price: debouncedPrice,
      }),
    enabled: previewEnabled,
    staleTime: 30_000,
    retry: false,
  })

  if (!open) return null

  const supervisorManagerOptions = (() => {
    if (customerMode !== 'choose_manager') return []
    const opts: { value: string; label: string }[] = []
    // «Я сам» — всегда первая опция (см. SPECIFICATION_ADMIN.md).
    if (profile?.id) {
      opts.push({ value: MANAGER_SELF_VALUE, label: t('orderDrawer.managerSelf') })
    }
    for (const m of managers) {
      if (m.id === profile?.id) continue
      opts.push({ value: m.id, label: m.full_name })
    }
    return opts
  })()

  const adminManagerOptions = managers.map((m) => ({
    value: m.id,
    label: m.full_name,
  }))

  function validatePhone(phone: string) {
    const digits = phone.replace(/\D/g, '')
    return digits.length === 11
  }

  // Резолв города считаем «завершённым» только когда геокодер уже попытался
  // обработать текущую строку адреса (resolvedForAddress совпадает с тем, что
  // сейчас в поле). Пока пользователь печатает — ошибок не показываем, чтобы
  // они не мелькали под открытым выпадающим списком саджеста.
  const resolveAttempted =
    address.trim().length >= 3 && resolvedForAddress === address.trim()

  const cityError: string | null = (() => {
    if (resolveAttempted && !resolvedCity && !resolving) {
      return t('orderDrawer.cityResolveFailed')
    }
    if (resolvedCity && !cityMatch) {
      const list = activeCitiesNames.join(', ') || '—'
      return t('orderDrawer.cityUnavailable', { city: resolvedCity, list })
    }
    return null
  })()

  // Заполнены ли минимальные поля для preview (для статуса sticky-панели).
  const hasMinimalForPreview =
    !!dealerId &&
    !!cityMatch &&
    (estimatedPrice ?? 0) > 0

  const previewReady = hasMinimalForPreview && previewQuery.data && !previewQuery.isError

  async function submit(asDraft = false) {
    setError('')
    const fe: Record<string, string> = {}

    if (customerMode === 'choose_company' && !companyId) {
      fe.company = t('orderDrawer.errSelectCompany')
    }
    if (customerMode !== 'self' && !managerId) {
      fe.manager = t('orderDrawer.errSelectManager')
    }
    if (!brand) fe.brand = t('orderDrawer.errSelectMake')
    if (!model) fe.model = t('orderDrawer.errSelectModel')

    // Поля ниже обязательны только для «Разместить», не для черновика
    if (!asDraft) {
      if (!estimatedPrice || estimatedPrice <= 0) {
        fe.estimatedPrice = t('orderDrawer.errEstimatedPrice')
      }
      if (!sellerName.trim()) fe.sellerName = t('orderDrawer.errSellerName')
      if (!validatePhone(sellerPhone)) {
        fe.sellerPhone = t('orderDrawer.errSellerPhone')
      }
      if (address.trim().length < 3) {
        fe.address = t('orderDrawer.errAddress')
      }
      if (cityError) fe.address = cityError
    }

    if (Object.keys(fe).length) {
      setFieldErrors(fe)
      setError(t('orderDrawer.errFillRequired'))
      return
    }
    setFieldErrors({})

    if (!profile?.id || !dealerId) {
      setError(t('orderDrawer.errNoUserData'))
      return
    }
    if (!asDraft && !cityMatch) {
      setError(cityError ?? t('orderDrawer.errCityUndetermined'))
      return
    }

    if (asDraft) setSavingDraft(true)
    else setSaving(true)
    try {
      const order = await createOrder({
        brand,
        model,
        vin: vin.trim() || null,
        vehicle_year: vehicleYear.trim()
          ? Number.parseInt(vehicleYear, 10)
          : null,
        license_plate: normalizeLicensePlate(licensePlate),
        address: address.trim() || '',
        address_city: cityMatch?.city ?? null,
        desired_price: estimatedPrice ?? null,
        seller_full_name: sellerName.trim() || null,
        seller_phone: sellerPhone || null,
        order_comment: comment.trim() || null,
        manager_id: dealerId,
        customer_company_id: customerCompanyId || null,
        contractor_company_id: null,
        inspector_id: null,
        status: asDraft ? 'DRAFT' : 'SEARCH',
      })
      onSaved()
      navigate(`/orders/${order.id}`)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('orderDrawer.errCreateOrder'))
    } finally {
      setSaving(false)
      setSavingDraft(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-[color:var(--stryzh-overlay-scrim-modal)]"
      role="presentation"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-[var(--stryzh-layout-entity-drawer-width)] max-w-full flex-col overflow-hidden border-l border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] shadow-[var(--stryzh-shadow-glow-strong)]"
        role="dialog"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[color:var(--stryzh-color-border-default)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          <h2 className="font-[family-name:var(--stryzh-font-family-space-grotesk)] text-[length:var(--stryzh-font-size-lg)] font-semibold tracking-[var(--stryzh-letter-spacing-space-grotesk)] text-[color:var(--stryzh-color-text-primary)]">
            {t('orderDrawer.title')}
          </h2>
          <Button
            variant="ghost"
            onClick={onClose}
            aria-label={t('orderDrawer.close')}
            className="stryzh-btn--close-icon"
          >
            ✕
          </Button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-[var(--stryzh-space-6)] py-[var(--stryzh-space-5)]">
          <div className="space-y-4">
            {/* Заказчик + Скоп */}
            {customerMode !== 'self' ? (
              <>
                <SectionLabel>{t('orderDrawer.sectionCustomer')}</SectionLabel>

                {customerMode === 'choose_company' ? (
                  <label className="flex flex-col gap-1">
                    <span
                      className={`stryzh-input-label${fieldErrors.company ? ' text-[color:var(--stryzh-color-semantic-error)]' : ''}`}
                    >
                      {t('orderDrawer.companyRequired')}
                    </span>
                    <Select
                      value={companyId}
                      onChange={(v) => {
                        setCompanyId(v)
                        setManagerId('')
                        setFieldErrors((p) => ({ ...p, company: '' }))
                      }}
                      options={companies.map((c) => ({
                        value: c.id,
                        label: c.name,
                      }))}
                      emptyLabel={t('orderDrawer.companyEmpty')}
                      invalid={!!fieldErrors.company}
                    />
                    {fieldErrors.company ? (
                      <span className="stryzh-input-error-text">
                        {fieldErrors.company}
                      </span>
                    ) : null}
                  </label>
                ) : (
                  // customer_supervisor: компания фиксирована, показываем read-only.
                  <label className="flex flex-col gap-1">
                    <span className="stryzh-input-label">{t('orderDrawer.company')}</span>
                    <div className="rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-fill-input)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-secondary)]">
                      {profile?.company_id
                        ? (companies.find((c) => c.id === profile.company_id)
                            ?.name ?? t('orderDrawer.yourCompany'))
                        : t('orderDrawer.yourCompany')}
                    </div>
                  </label>
                )}

                <label className="flex flex-col gap-1">
                  <span
                    className={`stryzh-input-label${fieldErrors.manager ? ' text-[color:var(--stryzh-color-semantic-error)]' : ''}`}
                  >
                    {t('orderDrawer.managerRequired')}
                  </span>
                  <Select
                    value={managerId}
                    onChange={(v) => {
                      setManagerId(v)
                      setFieldErrors((p) => ({ ...p, manager: '' }))
                    }}
                    options={
                      customerMode === 'choose_manager'
                        ? supervisorManagerOptions
                        : adminManagerOptions
                    }
                    emptyLabel={
                      customerMode === 'choose_company' && !companyId
                        ? t('orderDrawer.managerEmptyPickCompany')
                        : t('orderDrawer.managerEmpty')
                    }
                    disabled={
                      customerMode === 'choose_company' && !companyId
                    }
                    invalid={!!fieldErrors.manager}
                  />
                  {fieldErrors.manager ? (
                    <span className="stryzh-input-error-text">
                      {fieldErrors.manager}
                    </span>
                  ) : null}
                </label>

              </>
            ) : null}

            {/* Автомобиль */}
            <SectionLabel>{t('orderDrawer.sectionVehicle')}</SectionLabel>

            <label className="flex flex-col gap-1">
              <span
                className={`stryzh-input-label${fieldErrors.brand ? ' text-[color:var(--stryzh-color-semantic-error)]' : ''}`}
              >
                {t('orderDrawer.makeRequired')}
              </span>
              <VehicleSelect
                kind="brand"
                selectedName={brand}
                placeholder={t('orderDrawer.makeEmpty')}
                onChange={(v) => {
                  setBrand(v.name)
                  setBrandId(v.id)
                  // Смена марки сбрасывает выбранную модель.
                  setModel('')
                  setFieldErrors((p) => ({ ...p, brand: '' }))
                }}
                invalid={!!fieldErrors.brand}
              />
              {fieldErrors.brand ? (
                <span className="stryzh-input-error-text">
                  {fieldErrors.brand}
                </span>
              ) : null}
            </label>

            <label className="flex flex-col gap-1">
              <span
                className={`stryzh-input-label${fieldErrors.model ? ' text-[color:var(--stryzh-color-semantic-error)]' : ''}`}
              >
                {t('orderDrawer.modelRequired')}
              </span>
              <VehicleSelect
                kind="model"
                brandId={brandId}
                selectedName={model}
                placeholder={
                  brand
                    ? t('orderDrawer.modelEmpty')
                    : t('orderDrawer.modelEmptyPickMake')
                }
                onChange={(v) => {
                  setModel(v.name)
                  setFieldErrors((p) => ({ ...p, model: '' }))
                }}
                invalid={!!fieldErrors.model}
              />
              {fieldErrors.model ? (
                <span className="stryzh-input-error-text">
                  {fieldErrors.model}
                </span>
              ) : null}
            </label>

            <Input
              label="VIN"
              value={vin}
              onChange={(e) => setVin(e.target.value.toUpperCase().slice(0, 17))}
              placeholder="XTA210990Y2711234"
              maxLength={17}
            />
            {(() => {
              const hint = validateVinSoft(vin)
              return hint ? (
                <div className="mt-1 text-xs text-[color:var(--stryzh-color-warning,#e0a800)]">
                  {hint}
                </div>
              ) : null
            })()}

            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                label={t('orderDrawer.vehicleYear')}
                value={vehicleYear}
                onChange={(e) =>
                  setVehicleYear(e.target.value.replace(/\D/g, '').slice(0, 4))
                }
                placeholder="2018"
                inputMode="numeric"
                maxLength={4}
              />
              <Input
                label={t('orderDrawer.licensePlate')}
                value={licensePlate}
                onChange={(e) => setLicensePlate(formatLicensePlate(e.target.value))}
                placeholder="А 123 ВС 77"
              />
            </div>

            <Input
              label={t('orderDrawer.estimatedPrice')}
              value={estimatedPriceText}
              onChange={(e) => {
                const raw = e.target.value
                const parsed = parseGroupedIntInput(raw)
                setEstimatedPrice(parsed)
                setEstimatedPriceText(parsed == null ? '' : formatGroupedInt(parsed))
                setFieldErrors((p) => ({ ...p, estimatedPrice: '' }))
              }}
              placeholder="500 000"
              inputMode="numeric"
              error={fieldErrors.estimatedPrice}
            />

            {/* Продавец */}
            <SectionLabel>{t('orderDrawer.sectionSeller')}</SectionLabel>

            <Input
              label={t('orderDrawer.sellerName')}
              value={sellerName}
              onChange={(e) => {
                setSellerName(e.target.value)
                setFieldErrors((p) => ({ ...p, sellerName: '' }))
              }}
              placeholder={t('orderDrawer.sellerNamePlaceholder')}
              error={fieldErrors.sellerName}
            />

            <Input
              label={t('orderDrawer.sellerPhone')}
              value={sellerPhone}
              onChange={(e) => {
                setSellerPhone(formatPhoneInput(sellerPhone, e.target.value))
                setFieldErrors((p) => ({ ...p, sellerPhone: '' }))
              }}
              placeholder="+7 999 777 77 77"
              error={fieldErrors.sellerPhone}
            />

            {/* Место осмотра */}
            <SectionLabel>{t('orderDrawer.sectionInspectionPlace')}</SectionLabel>

            <AddressInput
              label={t('orderDrawer.address')}
              required
              value={address}
              onChange={(v) => {
                setAddress(v)
                // Если меняли строку — старое значение города больше не валидно.
                if (v.trim() !== resolvedForAddress) {
                  setResolvedCity(null)
                  setResolvedForAddress('')
                }
                setFieldErrors((p) => ({ ...p, address: '' }))
              }}
              placeholder={t('orderDrawer.addressPlaceholder')}
              error={fieldErrors.address}
              knownCities={activeCitiesNames}
              onSuggestionPicked={(item) => {
                if (item.locality) {
                  setResolvedCity(item.locality)
                  setResolvedForAddress(item.line1)
                }
                setFieldErrors((p) => ({ ...p, address: '' }))
              }}
            />

            {/* Подсказка под адресом: показываем только подтверждённый город
                или ошибку после попытки резолва. Текст «Определяем город…»
                намеренно не рендерим, чтобы не шуметь во время ввода. */}
            {resolveAttempted && resolvedCity && cityMatch ? (
              <p className="-mt-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
                {t('orderDrawer.cityResolved', { city: cityMatch!.city })}
              </p>
            ) : resolveAttempted && cityError ? (
              <p className="-mt-2 text-xs text-[color:var(--stryzh-color-semantic-error)]">
                {cityError}
              </p>
            ) : null}

            {/* Комментарий */}
            <SectionLabel>{t('orderDrawer.sectionComment')}</SectionLabel>

            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder={t('orderDrawer.commentPlaceholder')}
              className="w-full rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] px-3 py-2 text-sm text-[color:var(--stryzh-color-text-primary)] placeholder:text-[color:var(--stryzh-color-text-secondary)] focus:border-[color:var(--stryzh-color-accent-orange)] focus:outline-none"
            />

            {/* Подвал прокручиваемой части — отступ под sticky-панель, чтобы
                последние поля не «прижимались» к её верхней границе. */}
            <div className="h-2" />
          </div>
        </div>

        {/* Sticky-панель снизу */}
        <PreviewStickyPanel
          ready={!!previewReady}
          loading={previewQuery.isFetching && !previewQuery.data}
          error={previewQuery.error instanceof Error ? previewQuery.error.message : null}
          previewData={previewQuery.data ?? null}
          saving={saving}
          savingDraft={savingDraft}
          submitDisabled={saving || savingDraft || !previewReady}
          draftDisabled={saving || savingDraft || !brand || !model}
          onSubmit={() => void submit(false)}
          onDraft={() => void submit(true)}
          formError={error}
          popoverOpen={popoverOpen}
          setPopoverOpen={setPopoverOpen}
        />
      </aside>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sticky-панель снизу + popover «Детализация»
// ---------------------------------------------------------------------------

function PreviewStickyPanel({
  ready,
  loading,
  error,
  previewData,
  saving,
  savingDraft,
  submitDisabled,
  draftDisabled,
  onSubmit,
  onDraft,
  formError,
  popoverOpen,
  setPopoverOpen,
}: {
  ready: boolean
  loading: boolean
  error: string | null
  previewData: CalculateBillingResult | null
  saving: boolean
  savingDraft: boolean
  submitDisabled: boolean
  draftDisabled: boolean
  onSubmit: () => void
  onDraft: () => void
  formError: string
  popoverOpen: boolean
  setPopoverOpen: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)

  // Закрытие popover по клику вне панели/popover.
  useEffect(() => {
    if (!popoverOpen) return
    function onPointer(e: MouseEvent) {
      const target = e.target as Node
      if (popoverRef.current?.contains(target)) return
      if (containerRef.current?.contains(target)) return
      setPopoverOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPopoverOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [popoverOpen, setPopoverOpen])

  return (
    <div
      ref={containerRef}
      className="relative border-t border-[color:rgba(247,147,26,0.5)] bg-[color:rgba(247,147,26,0.04)] px-[var(--stryzh-space-6)] py-[var(--stryzh-space-4)]"
    >
      {ready && previewData && popoverOpen ? (
        <PreviewPopover
          ref={popoverRef}
          previewData={previewData}
        />
      ) : null}

      {ready && previewData ? (
        <div className="mb-3 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-[color:var(--stryzh-color-text-secondary)]">
              {t('orderDrawer.preliminary')}
            </div>
            <div className="mt-0.5 text-[20px] font-medium leading-tight text-white">
              {formatGroupedInt(previewData.totals.total_customer)} ₽
            </div>
          </div>
          <button
            type="button"
            className="text-sm font-medium text-[color:var(--stryzh-color-accent-orange)] transition-opacity hover:opacity-80"
            onClick={() => setPopoverOpen(!popoverOpen)}
          >
            {t('orderDrawer.details')} {popoverOpen ? '▴' : '▾'}
          </button>
        </div>
      ) : (
        <div className="mb-3 text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {loading
            ? t('orderDrawer.calculating')
            : error
              ? error
              : t('orderDrawer.fillToSeePrice')}
        </div>
      )}

      {formError ? (
        <p className="mb-2 text-xs text-[color:var(--stryzh-color-semantic-error)]">
          {formError}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={onDraft}
          disabled={draftDisabled}
          className="flex-1 rounded-[var(--stryzh-radius-md)] border border-[color:var(--stryzh-color-border-default)] bg-transparent px-4 py-2.5 text-sm font-semibold text-[color:var(--stryzh-color-text-secondary)] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {savingDraft ? t('orderDrawer.saving') : t('orderDrawer.draft')}
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={submitDisabled}
          className="flex-[2] rounded-[var(--stryzh-radius-md)] bg-[color:var(--stryzh-color-accent-orange)] px-4 py-2.5 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? t('orderDrawer.creating') : t('orderDrawer.submit')}
        </button>
      </div>
    </div>
  )
}

const PreviewPopover = forwardRef(PreviewPopoverImpl)

function PreviewPopoverImpl(
  {
    previewData,
  }: {
    previewData: CalculateBillingResult
  },
  ref: React.Ref<HTMLDivElement>,
) {
  const { t } = useTranslation()
  const lines = previewData.items
    .map((i) => ({
      label: serviceLabel(i.service),
      amount: i.amount_customer,
    }))
    .filter((l) => l.amount > 0)
  const total = previewData.totals.total_customer
  const idleHourly =
    previewData.idle.amount_customer === 0 && previewData.idle.total_hours === 0
      ? null
      : null
  // §8.7 preview не считает простой; ставку «X ₽/ч» отдельно показываем
  // справочно, если она прописана в snapshot. У preview нет idle_settings,
  // так что отображаем фиксированный текст из базового тарифа only when
  // backend начнёт прокидывать. Сейчас — выводим строку из snapshot как
  // плейсхолдер: значение придёт из items[*].amount, idle секция всегда 0.
  void idleHourly

  return (
    <div
      ref={ref}
      className="absolute bottom-full left-0 right-0 mb-2 origin-bottom animate-[stryzh-slide-up_220ms_ease-out] rounded-[var(--stryzh-radius-md)] border border-[color:rgba(247,147,26,0.4)] bg-[color:var(--stryzh-color-surface)] p-3 shadow-[var(--stryzh-shadow-glow-strong)]"
      style={{
        // inline keyframes via CSS variable fallback — animation определена
        // в style ниже на случай отсутствия `stryzh-slide-up` в theme.css.
      }}
    >
      <style>{`
        @keyframes stryzh-slide-up {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="space-y-1.5">
        {lines.map((l) => (
          <div
            key={l.label}
            className="flex items-baseline justify-between text-sm text-[color:var(--stryzh-color-text-primary)]"
          >
            <span>{l.label}</span>
            <span className="font-[family-name:var(--stryzh-font-family-jetbrains-mono)] tabular-nums">
              {formatGroupedInt(l.amount)} ₽
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-baseline justify-between border-t border-[color:var(--stryzh-color-border-default)] pt-2 text-sm font-semibold text-white">
        <span>{t('orderDrawer.total')}</span>
        <span className="font-[family-name:var(--stryzh-font-family-jetbrains-mono)] tabular-nums">
          {formatGroupedInt(total)} ₽
        </span>
      </div>
      {/* Простой сверх нормы: ставку показываем справочно — она хранится в
          tariff_snapshot и не зависит от заказа. preview-billing её сам не
          возвращает (idle = 0), но мы можем отрисовать прочерк, чтобы
          пользователь знал о существовании оплаты простоя. */}
      <div className="mt-2 text-[11px] text-[color:var(--stryzh-color-text-secondary)]">
        {t('orderDrawer.idleNote')}
      </div>
    </div>
  )
}
